import { z } from "zod";

export const roles = ["fan", "artist", "admin"] as const;
export type Role = (typeof roles)[number];

export interface ApiEnvelope<T> {
  data: T;
  traceId: string;
}

export interface ApiErrorPayload {
  error: {
    code: string;
    message: string;
  };
  traceId: string;
}

export interface User {
  id: string;
  role: Role;
  displayName: string;
  avatarUrl: string;
  bio: string;
  phoneVerified: boolean;
  realnameVerified: boolean;
  createdAt: string;
}

export interface Artist {
  id: string;
  userId: string;
  stageName: string;
  groupName: string;
  category: string;
  bio: string;
  avatarUrl: string;
  coverUrl: string;
  accent: string;
  heat: number;
  heatDelta: number;
  subscriberCount: number;
  isSubscribed: boolean;
  subscriptionExpiresAt?: string | null;
  officialCircleId: string;
  createdAt?: string | null;
}

export interface ArtistRankingEntry {
  artist: Artist;
  score: number;
  rank: number;
}

export type SubscriptionPlanSize = 1 | 3 | 5;
export type SubscriptionPlanId = "single" | "trio" | "quintet";

export interface SubscriptionPlan {
  id: SubscriptionPlanId;
  artistCount: SubscriptionPlanSize;
  unitPriceCents: number;
  subtotalCents: number;
  payRateBps: number;
  discountLabel: "原价" | "85折";
  totalCents: number;
  durationDays: 30;
  currency: "CNY";
  paymentMode: "local_demo";
  paymentNotice: string;
}

export interface SubscriptionOrderItem {
  artistId: string;
  artistName: string;
  unitPriceCents: number;
  priceCents: number;
  expiresAt: string;
}

export interface SubscriptionOrder {
  id: string;
  fanId: string;
  status: "simulated_paid";
  plan: SubscriptionPlan;
  items: SubscriptionOrderItem[];
  expiresAt: string;
  createdAt: string;
}

export interface Circle {
  id: string;
  artistId?: string | null;
  creatorId: string;
  name: string;
  kind: "official" | "community";
  category: "celebrity" | "influencer" | "cp";
  privacy: "public" | "closed";
  description: string;
  coverUrl: string;
  memberCount: number;
  activeScore: number;
  isFollowed: boolean;
  canPost: boolean;
  status: "pending" | "published" | "rejected";
  preferencePosition?: number | null;
  isHidden?: boolean;
}

export interface ArtistMessage {
  id: string;
  artistId: string;
  artistName: string;
  artistAvatarUrl: string;
  content: string;
  mediaUrl?: string | null;
  sequence: number;
  status: "published" | "scheduled" | "retracted";
  scheduledAt?: string | null;
  publishedAt?: string | null;
  retractedAt?: string | null;
  createdAt: string;
  readAt?: string | null;
  /** 已写入 message_reads 的唯一粉丝记录数，不代表实时或 Push 送达。 */
  readCount?: number;
  /** 查询时仍处于订阅状态的广播对象数，不代表客户端已送达。 */
  deliveryCount?: number;
}

/** Direct Socket.IO payload for `artist:message-retracted`. */
export type RetractedArtistMessage = ArtistMessage & {
  status: "retracted";
  retractedAt: string;
};

export interface Conversation {
  artist: Artist;
  latestMessage?: ArtistMessage | null;
  /** The newest private note sent by the authenticated fan to this artist. */
  latestFanMessage?: FanToArtistMessage | null;
  unreadCount: number;
}

/** A private message sent by one subscribed fan to an artist inbox. */
export interface FanToArtistMessage {
  id: string;
  artistId: string;
  fanId: string;
  fanDisplayName: string;
  fanAvatarUrl: string;
  content: string;
  status: "unread" | "read";
  readAt?: string | null;
  createdAt: string;
}

export interface FanToArtistMessagePage {
  items: FanToArtistMessage[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface FanToArtistMessageReadResult {
  messageIds: string[];
  readAt: string;
  unreadCount: number;
}

export interface CampaignDetails {
  activityName: string;
  activityType: "offline" | "online";
  method: "merch" | "unlock" | "savings" | "pickup";
  sessionTime?: string;
  meetingPoint?: string;
  pickupNote?: string;
  materials?: { id: string; name: string; spec: string; total: number }[];
  tiers?: { at: number; label: string }[];
  platformUrl?: string;
  unitPriceCents?: number;
  target: string;
  deadline: string;
  coHosts?: string;
  itemName?: string;
  targetQuantity?: number;
  unlockDescription?: string;
  goalCount?: number;
  fundPurpose?: string;
  goalAmountCents?: number;
}

export interface CampaignJoinState {
  post: Post;
  participantCount: number;
  shares: number;
  materials: { id: string; name: string; spec: string; total: number; taken: number }[];
  mine: { itemId: string | null; code: string | null; quantity: number; createdAt: string } | null;
  recent: { name: string; createdAt: string }[];
  closed: boolean;
}

export interface Post {
  authorCircleRole?: "owner" | "admin" | "member";
  id: string;
  circleId: string;
  circleName: string;
  authorId: string;
  authorName: string;
  authorAvatarUrl: string;
  type: "normal" | "campaign";
  content: string;
  mediaUrls: string[];
  status: "draft" | "published" | "pending" | "rejected" | "hidden";
  campaign?: CampaignDetails | null;
  likeCount: number;
  commentCount: number;
  isLiked: boolean;
  isSaved: boolean;
  campaignParticipationCount: number;
  isParticipating: boolean;
  createdAt: string;
}

export interface Product {
  createdAt?: string;
  listingDetails?: { condition?: string | undefined; shippingPromise?: string | undefined; virtualKind?: string | undefined; deliveryMethod?: string | undefined };
  id: string;
  sellerId: string;
  sellerName: string;
  artistId?: string | null;
  title: string;
  description: string;
  type: "physical" | "virtual";
  imageUrl: string;
  imageUrls: string[];
  priceCents: number;
  shippingCents: number;
  stock: number;
  sales: number;
  isCarpool: boolean;
  deadline?: string | null;
  refundAllowed: boolean;
  status: "active" | "hidden";
  closedAt?: string | null;
  closeReason?: "expired" | "seller_closed" | null;
}

export interface CartItem extends Product {
  quantity: number;
  selected: boolean;
}

export interface Order {
  id: string;
  buyerId: string;
  buyerName: string;
  sellerId: string;
  sellerName: string;
  status: "pending" | "paid" | "shipping" | "completed" | "refunding" | "refunded";
  totalCents: number;
  shippingCents: number;
  items: Array<{ productId: string; title: string; quantity: number; unitPriceCents: number }>;
  carrier?: string | null;
  trackingNumber?: string | null;
  shippedAt?: string | null;
  completedAt?: string | null;
  refundEligible?: boolean;
  refundReason?: string | null;
  refundResolution?: string | null;
  review?: { rating: number; content: string; createdAt: string } | null;
  createdAt: string;
}

export interface ModerationItem {
  id: string;
  targetType: "post" | "campaign" | "circle" | "message";
  targetId: string;
  title: string;
  summary: string;
  status: "pending" | "approved" | "rejected";
  submittedBy: string;
  submittedById: string;
  createdAt: string;
}

export interface AdminMutationResult {
  targetId: string;
  status: string;
  changed: boolean;
  userId?: string;
}

export interface CirclePreference {
  circleId: string;
  position: number;
  hidden: boolean;
}

export interface PostInteractionResult {
  postId: string;
  isLiked: boolean;
  isSaved: boolean;
  likeCount: number;
}

export interface CampaignParticipationResult {
  postId: string;
  participating: boolean;
  participantCount: number;
}

export interface SystemNotification {
  id: string;
  userId: string;
  type: "moderation_submitted" | "moderation_approved" | "moderation_rejected" | "market_update";
  title: string;
  body: string;
  status: "unread" | "read";
  readAt?: string | null;
  createdAt: string;
  target: {
    type: ModerationItem["targetType"] | "product";
    id: string;
  };
}

export interface NotificationReadResult {
  notificationIds: string[];
  unreadCount: number;
}

export interface AdminOverview {
  metrics: {
    users: number;
    artists: number;
    activeSubscriptions: number;
    messagesToday: number;
    pendingModeration: number;
    orders: number;
  };
  moderation: ModerationItem[];
  recentAudit: Array<{
    id: string;
    actorName: string;
    action: string;
    target: string;
    traceId: string;
    createdAt: string;
  }>;
  health?: {
    realtime: boolean;
    pushConfigured: boolean;
    scheduler: boolean;
  };
}

export const demoLoginSchema = z.object({
  userId: z.string().min(1).max(80),
});

export const fanRegistrationSchema = z.object({
  displayName: z.string().trim().min(1, "请输入昵称").max(30, "昵称最多 30 个字"),
  phone: z.string().trim().regex(/^\d{11}$/, "手机号需要填写 11 位数字"),
});

export const subscriptionMutationSchema = z.object({
  artistId: z.string().min(1).max(80),
  action: z.enum(["subscribe", "unsubscribe", "block", "unblock"]),
});

export const createSubscriptionOrderSchema = z.object({
  artistIds: z.array(z.string().min(1).max(80)).min(1).max(5),
}).superRefine((input, context) => {
  if (![1, 3, 5].includes(input.artistIds.length)) {
    context.addIssue({ code: "custom", path: ["artistIds"], message: "订阅套餐仅支持选择 1、3 或 5 位明星" });
  }
  if (new Set(input.artistIds).size !== input.artistIds.length) {
    context.addIssue({ code: "custom", path: ["artistIds"], message: "同一位明星不能重复选择" });
  }
});

export const publishMessageSchema = z.strictObject({
  content: z.string().trim().min(1).max(2000),
  mediaUrl: z.url().optional().or(z.literal("")),
  scheduledAt: z.iso.datetime().optional(),
});

export const readMessagesSchema = z.object({
  messageIds: z.array(z.string().min(1).max(100)).min(1).max(100),
});

export const createFanToArtistMessageSchema = z.object({
  artistId: z.string().min(1).max(80),
  content: z.string().trim().min(1, "留言内容不能为空").max(1_000, "留言最多 1000 个字"),
});

export const fanArtistMessageHistoryQuerySchema = z.object({
  before: z.string().min(1).max(100).optional(),
});

export const artistInboxQuerySchema = z.object({
  before: z.string().min(1).max(100).optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
}).superRefine((input, context) => {
  if (input.from && input.to && input.from > input.to) {
    context.addIssue({ code: "custom", path: ["from"], message: "开始时间不能晚于结束时间" });
  }
});

export const readFanToArtistMessagesSchema = z.object({
  messageIds: z.array(z.string().min(1).max(100)).min(1).max(100),
});

export const readNotificationsSchema = z.object({
  notificationIds: z.array(z.string().min(1).max(100)).min(1).max(100),
});

const campaignMethodFields = [
  "itemName",
  "targetQuantity",
  "unlockDescription",
  "goalCount",
  "fundPurpose",
  "goalAmountCents", "sessionTime", "meetingPoint", "materials",
] as const;
type CampaignMethodField = (typeof campaignMethodFields)[number];

const campaignMethodRequirements = {
  merch: ["itemName", "targetQuantity"],
  unlock: ["unlockDescription", "goalCount"],
  savings: ["fundPurpose", "goalAmountCents"],
  pickup: ["sessionTime", "meetingPoint", "materials"],
} as const satisfies Record<CampaignDetails["method"], readonly CampaignMethodField[]>;

const campaignMethodLabels: Record<CampaignDetails["method"], string> = {
  merch: "物料",
  unlock: "解锁",
  savings: "储蓄",
  pickup: "物料领取",
};

const campaignFieldLabels: Record<CampaignMethodField, string> = {
  itemName: "物料名称",
  targetQuantity: "目标数量",
  unlockDescription: "解锁内容",
  goalCount: "目标人数",
  fundPurpose: "资金用途",
  goalAmountCents: "目标金额",
  sessionTime: "场次时间", meetingPoint: "集合点", materials: "物料清单",
};

const campaignDetailsSchema = z.object({
  sessionTime: z.string().trim().min(1).max(100).optional(),
  meetingPoint: z.string().trim().min(1).max(200).optional(),
  pickupNote: z.string().trim().max(300).optional(),
  materials: z.array(z.object({ id: z.string().regex(/^[a-zA-Z0-9_-]{1,60}$/), name: z.string().trim().min(1).max(80), spec: z.string().trim().max(200), total: z.number().int().min(1).max(100000) })).min(1).max(20).optional(),
  tiers: z.array(z.object({ at: z.number().int().min(1).max(100000000), label: z.string().trim().min(1).max(100) })).max(10).optional(),
  platformUrl: z.url().refine(value => /^https?:\/\//.test(value), "仅支持 HTTP/HTTPS 链接").optional(),
  unitPriceCents: z.number().int().min(1).max(100000000).optional(),
  activityName: z.string().trim().min(2).max(80),
  activityType: z.enum(["offline", "online"]),
  method: z.enum(["merch", "unlock", "savings", "pickup"]),
  target: z.string().trim().min(1).max(200),
  deadline: z.iso.datetime().transform((value) => new Date(value).toISOString()),
  coHosts: z.string().trim().max(200).optional(),
  itemName: z.string().trim().min(1).max(80).optional(),
  targetQuantity: z.number().int().min(1).max(10_000_000).optional(),
  unlockDescription: z.string().trim().min(1).max(500).optional(),
  goalCount: z.number().int().min(1).max(100_000_000).optional(),
  fundPurpose: z.string().trim().min(1).max(500).optional(),
  goalAmountCents: z.number().int().min(1).max(1_000_000_000).optional(),
}).superRefine((campaign, context) => {
  if (campaign.method === "pickup" && campaign.activityType !== "offline") context.addIssue({ code: "custom", path: ["activityType"], message: "物料领取必须为线下应援" });
  if (campaign.materials && new Set(campaign.materials.map(item => item.id)).size !== campaign.materials.length) context.addIssue({ code: "custom", path: ["materials"], message: "物料编号不能重复" });
  if (campaign.tiers && (campaign.method !== "unlock" || campaign.tiers.some((tier, index) => index > 0 && tier.at <= campaign.tiers![index - 1]!.at))) context.addIssue({ code: "custom", path: ["tiers"], message: "解锁阶梯须按人数递增" });
  if (campaign.unitPriceCents && !["merch", "savings"].includes(campaign.method)) context.addIssue({ code: "custom", path: ["unitPriceCents"], message: "免费参与不能设置单价" });
  const requiredFields = new Set<CampaignMethodField>(campaignMethodRequirements[campaign.method]);
  for (const field of campaignMethodFields) {
    const present = campaign[field] !== undefined;
    if (requiredFields.has(field) && !present) {
      context.addIssue({
        code: "custom",
        path: [field],
        message: `${campaignMethodLabels[campaign.method]}企划必须填写${campaignFieldLabels[field]}`,
      });
    } else if (!requiredFields.has(field) && present) {
      context.addIssue({
        code: "custom",
        path: [field],
        message: `${campaignMethodLabels[campaign.method]}企划不能填写${campaignFieldLabels[field]}`,
      });
    }
  }
});

export const createPostSchema = z.object({
  circleId: z.string().min(1).max(80),
  type: z.enum(["normal", "campaign"]),
  content: z.string().trim().min(1).max(5000),
  mediaUrls: z.array(z.url()).max(9).default([]),
  publishMode: z.enum(["draft", "publish"]).default("publish"),
  campaign: campaignDetailsSchema.optional(),
});

export const artistQuerySchema = z.object({
  q: z.string().trim().max(80).optional(),
  category: z.string().trim().max(40).optional(),
  subscribed: z.enum(["true", "false"]).optional(),
  sort: z.enum(["heat", "new", "subscribers", "name"]).default("heat"),
});

export const circleQuerySchema = z.object({
  q: z.string().trim().max(80).optional(),
  category: z.enum(["celebrity", "influencer", "cp"]).optional(),
  kind: z.enum(["official", "community"]).optional(),
  privacy: z.enum(["public", "closed"]).optional(),
  followed: z.enum(["true", "false"]).optional(),
  includeHidden: z.enum(["true", "false"]).default("false"),
  sort: z.enum(["preference", "active", "members", "new"]).default("preference"),
});

export const circlePreferencesSchema = z.object({
  items: z.array(z.object({
    circleId: z.string().min(1).max(80),
    position: z.number().int().min(0).max(10_000),
    hidden: z.boolean(),
  })).max(200),
}).superRefine((input, context) => {
  if (new Set(input.items.map((item) => item.circleId)).size !== input.items.length) {
    context.addIssue({ code: "custom", path: ["items"], message: "圈子偏好不能重复" });
  }
});

export const postInteractionSchema = z.object({ action: z.enum(["like", "unlike", "save", "unsave"]) });
export const updateProfileSchema = z.object({
  displayName: z.string().trim().min(2).max(30),
  bio: z.string().trim().max(200),
});
export const campaignParticipationSchema = z.object({ action: z.enum(["join", "leave"]), itemId: z.string().max(60).optional(), quantity: z.number().int().min(1).max(999).optional() });
export type CampaignParticipationInput = z.infer<typeof campaignParticipationSchema>;

export const createCircleSchema = z.object({
  name: z.string().trim().min(2).max(40),
  description: z.string().trim().min(10).max(500),
  category: z.enum(["celebrity", "influencer", "cp"]),
  privacy: z.enum(["public", "closed"]),
  coFounderNames: z.array(z.string().trim().min(1)).max(9).default([]),
}).superRefine((input, context) => {
  if (new Set(input.coFounderNames).size !== input.coFounderNames.length) {
    context.addIssue({ code: "custom", path: ["coFounderNames"], message: "联合发起人不能重复" });
  }
});

export const circleMembershipSchema = z.object({
  action: z.enum(["follow", "unfollow"]),
});

export const cartMutationSchema = z.object({
  productId: z.string().min(1).max(80),
  quantity: z.number().int().min(0).max(99),
  selected: z.boolean().optional(),
});

export const createProductSchema = z.object({
  listingDetails: z.object({ condition: z.string().trim().max(40).optional(), shippingPromise: z.string().trim().max(80).optional(), virtualKind: z.string().trim().max(80).optional(), deliveryMethod: z.string().trim().max(100).optional() }).optional(),
  title: z.string().trim().min(2).max(100),
  description: z.string().trim().min(2).max(2_000),
  type: z.enum(["physical", "virtual"]),
  imageUrls: z.array(z.url()).min(1).max(9),
  priceCents: z.number().int().min(0).max(100_000_000),
  shippingCents: z.number().int().min(0).max(1_000_000).default(0),
  stock: z.number().int().min(1).max(1_000_000),
  artistId: z.string().min(1).max(80).nullable().optional(),
  isCarpool: z.boolean().default(false),
  deadline: z.iso.datetime().nullable().optional(),
  refundAllowed: z.boolean().default(false),
}).superRefine((input, context) => {
  if (input.isCarpool && !input.deadline) {
    context.addIssue({ code: "custom", path: ["deadline"], message: "发车需要填写截止时间" });
  }
  if (input.type === "virtual" && !input.deadline) {
    context.addIssue({ code: "custom", path: ["deadline"], message: "虚拟商品需要填写最晚交付日期" });
  }
  if (input.deadline && new Date(input.deadline).getTime() <= Date.now()) {
    context.addIssue({ code: "custom", path: ["deadline"], message: "截止或交付时间需要晚于当前时间" });
  }
  if (input.type === "virtual" && input.shippingCents !== 0) {
    context.addIssue({ code: "custom", path: ["shippingCents"], message: "虚拟商品不能收取运费" });
  }
});

export const shipOrderSchema = z.object({
  carrier: z.string().trim().min(2).max(40),
  trackingNumber: z.string().trim().min(4).max(80).regex(/^[A-Za-z0-9-]+$/, "运单号格式不正确"),
});

export const checkoutSchema = z.object({
  productIds: z.array(z.string().min(1).max(80)).min(1).max(50),
});

export const moderationActionSchema = z.object({
  action: z.enum(["approve", "reject"]),
  reason: z.string().trim().max(500).optional(),
}).superRefine((input, context) => {
  if (input.action === "reject" && !input.reason) {
    context.addIssue({ code: "custom", path: ["reason"], message: "驳回时必须填写原因" });
  }
});

export const adminAccountStatusSchema = z.object({ status: z.enum(["active", "disabled"]) });
export const adminPostVisibilitySchema = z.object({ hidden: z.boolean() });

export const pushSubscriptionSchema = z.object({
  endpoint: z.url(),
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    p256dh: z.string().min(1),
    auth: z.string().min(1),
  }),
});

export type DemoLoginInput = z.infer<typeof demoLoginSchema>;
export type FanRegistrationInput = z.infer<typeof fanRegistrationSchema>;
export type CreateSubscriptionOrderInput = z.infer<typeof createSubscriptionOrderSchema>;
export type PublishMessageInput = z.infer<typeof publishMessageSchema>;
export type CreateFanToArtistMessageInput = z.infer<typeof createFanToArtistMessageSchema>;
export type FanArtistMessageHistoryQueryInput = z.infer<typeof fanArtistMessageHistoryQuerySchema>;
export type ArtistInboxQueryInput = z.infer<typeof artistInboxQuerySchema>;
export type CreatePostInput = z.infer<typeof createPostSchema>;
export type ArtistQueryInput = z.infer<typeof artistQuerySchema>;
export type CircleQueryInput = z.infer<typeof circleQuerySchema>;
export type CirclePreferencesInput = z.infer<typeof circlePreferencesSchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type CreateCircleInput = z.infer<typeof createCircleSchema>;
export type CreateProductInput = z.infer<typeof createProductSchema>;
export type ShipOrderInput = z.infer<typeof shipOrderSchema>;

export const realtimeEvents = {
  artistMessage: "artist:message",
  messageRetracted: "artist:message-retracted",
  moderationUpdated: "moderation:updated",
  artistFanMessage: "artist:fan-message",
  subscriptionUpdated: "subscription:updated",
  ordersUpdated: "orders:updated",
} as const;

export interface ArtistRealtimeEventPayloads {
  "artist:message": ArtistMessage;
  "artist:message-retracted": RetractedArtistMessage;
  "artist:fan-message": FanToArtistMessage;
  "moderation:updated": ModerationItem;
}

export interface ContentReport {
  id: string;
  reporterId: string;
  targetType: "post" | "message";
  targetId: string;
  content: string;
  reason: string;
  status: "pending" | "upheld" | "dismissed";
  resolution: string | null;
  createdAt: string;
  reviewedAt: string | null;
}
export const contentReportSchema = z.object({
  targetType: z.enum(["post", "message"]),
  targetId: z.string().min(1).max(100),
  reason: z.string().trim().min(2, "请填写举报原因").max(500),
});
export const reportResolutionSchema = z.object({
  status: z.enum(["upheld", "dismissed"]),
  resolution: z.string().trim().min(2, "请填写处理说明").max(500),
});

export const orderActionSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("confirm") }),
  z.object({ action: z.literal("refund"), reason: z.string().trim().min(2).max(500) }),
  z.object({ action: z.literal("approve-refund"), reason: z.string().trim().min(2).max(500) }),
  z.object({ action: z.literal("reject-refund"), reason: z.string().trim().min(2).max(500) }),
  z.object({ action: z.literal("review"), rating: z.number().int().min(1).max(5), content: z.string().trim().min(2).max(500) }),
]);
export type OrderActionInput = z.infer<typeof orderActionSchema>;
